import {Grid, GridItem} from '@/components/ui/grid-atoms/GridAtoms'
import {InputSelectPatient} from '@/features/patients/components/input-select-patient/InputSelectPatient'
import {Controller, useController, useFormContext, useFormState, useWatch} from 'react-hook-form'
import {useEffect} from 'react'
import {useMissedAccessesCount} from '../../queries/useMissedAccessesCount'
import {CreateAbsenceFormValidationSchema, MAX_DAILY_ABSENCES} from '../../formSchemas'
import {communicatedAttachmentAcceptedFileTypes} from '../../utils'
import {TKey, useTranslation} from '@/translations/i18n'
import {InputSelectProjectByPatient} from '@/features/projects/components/input-select-project-by-patient/InputSelectProjectByPatient'
import {InputSelectUser} from '@/features/users/components/input-select-user/InputSelectUser'
import {InputSelectAccessType} from '@/features/access-types/components/input-select-access-type/InputSelectAccessType'
import {RadioField} from '@/components/commons/radio-field/RadioField'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {Label} from '@/components/ui/label/Label'
import {FormFieldBox} from '@/components/ui/form-atoms/FormAtoms'
import {InputHelpText} from '@/components/ui/input-help-text/InputHelpText'
import {projectSectorToLabel} from '@/features/projects/utils'
import {InputDate} from '@/components/commons/input-date/InputDate'
import {Text} from '@/components/ui/text/Text'
import {useUploadDocument} from '@/services/useUploadDocument'
import {useDeleteDocument} from '@/services/useDeleteDocument'
import {toast} from '@/utilities/toast/toast'
import {FileUploader} from '@/components/commons/file-upload/FileUpload'
import {Attachment02Icon} from '@/components/ui/icon/Icon'
import {MissedAccessesDisplay} from '../missed-accesses-display/MissedAccessesDisplay'
import {InputText} from '@/components/commons/input-text/InputText'

const communicationTimingToLabel = {
    within_48_hours: 'commons:within_48_hours',
    over_48_hours: 'commons:over_48_hours'
} as const satisfies Record<NonNullable<CreateAbsenceFormValidationSchema['communicatedOnTime']>, TKey>

interface AbsenceFormProps {
    isEdit?: boolean
}

export const AbsenceForm = ({isEdit = false}: AbsenceFormProps) => {
    const {t} = useTranslation()
    const form = useFormContext<CreateAbsenceFormValidationSchema>()
    const patient = useWatch({control: form.control, name: 'patient'})
    const project = useWatch({control: form.control, name: 'project'})
    const date = useWatch({control: form.control, name: 'date'})
    const communicatedOnTime = useWatch({control: form.control, name: 'communicatedOnTime'})
    const consultant = useWatch({control: form.control, name: 'consultant'})
    const numberOfAbsences = useWatch({control: form.control, name: 'numberOfAbsences'})

    const {count: missedAccessesCount, isAvailable: isMissedAccessesCountAvailable} = useMissedAccessesCount({
        date,
        projectId: project?.id
    })
    const maxAbsences = isMissedAccessesCountAvailable ? Math.max(0, MAX_DAILY_ABSENCES - missedAccessesCount) : 1
    const isDayFull = isMissedAccessesCountAvailable && maxAbsences === 0
    const {errors} = useFormState({control: form.control, name: 'numberOfAbsences'})

    // Feed the already-recorded per-day count into the form (creation only) so the schema's
    // superRefine can hard-block when existing + requested absences exceed the daily cap.
    // On edit we never add absences, so the count must stay 0 to avoid blocking updates.
    useEffect(() => {
        if (isEdit) return
        form.setValue('existingAbsencesCount', isMissedAccessesCountAvailable ? missedAccessesCount : 0, {
            shouldValidate: true
        })
    }, [isEdit, isMissedAccessesCountAvailable, missedAccessesCount])

    // Clamp the requested amount to the remaining slots while at least one slot is free;
    // when the day is full the superRefine surfaces the blocking error instead.
    useEffect(() => {
        if (isEdit) return
        if (maxAbsences >= 1 && numberOfAbsences && numberOfAbsences > maxAbsences) {
            form.setValue('numberOfAbsences', maxAbsences, {shouldValidate: true})
        }
    }, [isEdit, maxAbsences, numberOfAbsences])

    const onChangePatientSideEffect = () => {
        form.resetField('project')
    }

    const controller = useController({control: form.control, name: 'communicatedAttachment'})
    const uploadDocument = useUploadDocument({
        onSuccess: response => {
            controller.field.onChange(response, {shouldDirty: true, shouldValidate: true})
            toast.success(t('commons:upload_success'))
        },
        onError: () => toast.error(t('commons:upload_error'))
    })
    const deleteDocument = useDeleteDocument({
        onSuccess: () => {
            controller.field.onChange(null, {shouldDirty: true, shouldValidate: true})
        }
    })

    return (
        <Grid columns={2} columnGap={4} rowGap={6} fullWidth>
            <Controller
                control={form.control}
                name="date"
                render={({field, fieldState}) => (
                    <InputDate
                        label={t('commons:date')}
                        value={field.value}
                        errorMessage={fieldState.error?.message}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        fullWidth
                        placeholder={'GG/MM/AAAA'}
                    />
                )}
            />

            <MissedAccessesDisplay />

            <Controller
                control={form.control}
                name="patient"
                render={({field, fieldState}) => (
                    <InputSelectPatient
                        label={t('commons:patient')}
                        placeholder={t('agenda:select_patient')}
                        onChange={patient => {
                            field.onChange(patient)
                            onChangePatientSideEffect()
                        }}
                        value={field.value}
                        errorMessage={fieldState.error?.message}
                        hideBadge
                        hideDate
                        disabled={isEdit}
                    />
                )}
            />

            <Controller
                control={form.control}
                name="project"
                render={({field, fieldState}) => (
                    <InputSelectProjectByPatient
                        disabled={!patient || isEdit}
                        label={t('commons:project_number')}
                        placeholder={t('commons:select_a_project')}
                        onChange={field.onChange}
                        errorMessage={fieldState.error?.message}
                        patientId={patient?.id}
                        value={field.value}
                    />
                )}
            />

            {!isEdit && (
                <>
                    <InputText
                        type="number"
                        min={1}
                        max={maxAbsences}
                        disabled={!date || !patient || !project || isDayFull}
                        label={t('accesses:number_of_absences')}
                        placeholder={t('accesses:number_of_absences_placeholder')}
                        errorMessage={errors.numberOfAbsences?.message}
                        helpText={
                            !isDayFull && isMissedAccessesCountAvailable && maxAbsences < MAX_DAILY_ABSENCES
                                ? t('accesses:number_of_absences_max', {count: maxAbsences})
                                : undefined
                        }
                        {...form.register('numberOfAbsences')}
                    />
                    <div />
                </>
            )}

            <Controller
                control={form.control}
                name="consultant"
                render={({field, fieldState}) => (
                    <InputSelectUser
                        label={t('commons:consultant')}
                        placeholder={t('agenda:select_consultant')}
                        onChange={field.onChange}
                        errorMessage={fieldState.error?.message}
                        value={field.value}
                    />
                )}
            />

            <Controller
                control={form.control}
                name="accessType"
                render={({field, fieldState}) => (
                    <InputSelectAccessType
                        label={t('accesses:access_type')}
                        placeholder={t('agenda:select_access_type_placeholder')}
                        onChange={field.onChange}
                        errorMessage={fieldState.error?.message}
                        value={field.value}
                        disabled={!consultant}
                        specializationId={consultant?.specializationId ?? undefined}
                    />
                )}
            />

            <GridItem spanColumn={2}>
                <Controller
                    control={form.control}
                    name="sector"
                    render={({field, fieldState}) => (
                        <Flexbox direction="column" gap={1.5} fullWidth>
                            <Label>{t('accesses:mode')}</Label>
                            <Grid columns={2} columnGap={4} fullWidth>
                                {Object.entries(projectSectorToLabel)
                                    .filter(([value]) => ['outpatient', 'domiciliary'].includes(value))
                                    .map(([value, label]) => (
                                        <FormFieldBox
                                            key={value}
                                            isSelected={field.value === value}
                                            hasError={!!fieldState.error?.message}
                                        >
                                            <RadioField
                                                label={t(label)}
                                                name={field.name}
                                                checked={field.value === value}
                                                onChange={() => field.onChange(value)}
                                                value={value}
                                            />
                                        </FormFieldBox>
                                    ))}
                            </Grid>
                            {fieldState.error?.message && <InputHelpText error={fieldState.error?.message} />}
                        </Flexbox>
                    )}
                />
            </GridItem>

            <GridItem spanColumn={2}>
                <Controller
                    control={form.control}
                    name="communicatedOnTime"
                    render={({field, fieldState}) => (
                        <Flexbox direction="column" gap={1.5} fullWidth>
                            <Flexbox gap={1.5} fullWidth>
                                <Label>{t('accesses:absence_communication')}</Label>
                                <Text size="textSm" palette="neutral" shade="500">
                                    {t('commons:optional')}
                                </Text>
                            </Flexbox>
                            <Grid columns={2} columnGap={4} fullWidth>
                                {Object.entries(communicationTimingToLabel).map(([value, label]) => (
                                    <FormFieldBox
                                        key={value}
                                        isSelected={field.value === value}
                                        hasError={!!fieldState.error?.message}
                                    >
                                        <RadioField
                                            label={t(label)}
                                            name={field.name}
                                            checked={field.value === value}
                                            onChange={() => field.onChange(value)}
                                            value={value}
                                        />
                                    </FormFieldBox>
                                ))}
                            </Grid>
                            {fieldState.error?.message && <InputHelpText error={fieldState.error?.message} />}
                        </Flexbox>
                    )}
                />
            </GridItem>

            {communicatedOnTime === 'within_48_hours' && (
                <GridItem spanColumn={2}>
                    <FileUploader
                        document={controller.field.value}
                        isLoading={uploadDocument.isPending || deleteDocument.isPending}
                        inputFileProps={{
                            acceptedFiles: communicatedAttachmentAcceptedFileTypes,
                            maxSizeInMB: 8,
                            placeholder: t('projects:attach_file'),
                            onAcceptFiles: file => {
                                const formData = new FormData()
                                formData.append(`file`, file)
                                uploadDocument.mutate(formData)
                            },
                            typeIcon: <Attachment02Icon size={15} />,
                            hideButton: true,
                            width: '100%'
                        }}
                        onDeleteFile={deleteDocument.mutate}
                        dropDownMenuProps={{
                            triggerWidth: '100%'
                        }}
                    />
                </GridItem>
            )}
        </Grid>
    )
}
